import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import { observer } from 'mobx-react';
import { Label } from '@components/index';
import { fonts } from '@theme/fontsCustomer';
import { CalendarStartIcon } from '@icons/CalendarStartIcon';
import { PersonIcon } from '@icons/PersonIcon';
import { PlugIcon } from '@icons/PlugIcon';
import { WorkflowIcon } from '@icons/WorkflowIcon';
import { dateTimeFormat } from '@utils/dateUtil';
import type { SubscriptionConnectionModel } from '@repositories/subscriptionConnectionRepository/models/SubscriptionConnectionModel';
import { ConnectionStatusButton } from '../components/ConnectionStatusButton';

interface Props {
  connection: SubscriptionConnectionModel;
  isConnectionFetching: boolean;
  connectMutatingCount: number;
  onClickConnectBtn: () => void;
  onClickDisconnectBtn: () => void;
  onClickModifyBtn: () => void;
}

export const ConnectionInfo = observer(
  ({
    connection,
    isConnectionFetching,
    connectMutatingCount,
    onClickConnectBtn,
    onClickDisconnectBtn,
    onClickModifyBtn,
  }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
      <Container>
        <Title>{t('Subscrib_Connect_Detail')}</Title>
        <DetailWrapper>
          <ConnectionStatusButton
            connection={connection}
            connectMutatingCount={connectMutatingCount}
            isConnectionFetching={isConnectionFetching}
            onClickConnectBtn={onClickConnectBtn}
            onClickDisconnectBtn={onClickDisconnectBtn}
            onClickModifyBtn={onClickModifyBtn}
          />
          <InfoRow>
            <InfoName>
              <PlugIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
              {t('Subscrib_Connect_Connector')}
            </InfoName>
            <FlexBox>
              <InfoValue>{connection.connector?.name ?? '-'}</InfoValue>
              <Label variant="contain" color={connection.connector?.usageMethodLabelColor ?? 'green'}>
                {connection.connector?.usageCollectMethod}
              </Label>
            </FlexBox>
          </InfoRow>
          <InfoRow>
            <InfoName>
              <PersonIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
              {t('Workflow_Main_06')}
            </InfoName>
            <InfoValue>{connection.creator?.name ?? '-'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoName>
              <CalendarStartIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
              {t('Subscrib_Connect_02')}
            </InfoName>
            <InfoValue>{dateTimeFormat(connection.createdDate)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoName>
              <WorkflowIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
              {t('Subscrib_Connect_03')}
            </InfoName>
            <InfoValue>{connection.workflowCount ?? 0}</InfoValue>
          </InfoRow>
        </DetailWrapper>
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Title = styled.strong`
  ${fonts.Headline7};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  margin-bottom: 12px;
`;

const DetailWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 24px 28px;
  background-color: ${({ theme: { colors } }) => colors['bg-gray-lighter']};
  border: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  border-radius: 5px;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.02), 0px 0px 3px 0px rgba(0, 0, 0, 0.07);
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const InfoName = styled.span`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
`;

const InfoValue = styled.span`
  ${fonts.Headline8};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const FlexBox = styled.div`
  display: flex;
  gap: 5px;
`;
